import { mountStoreDevtool } from "simple-zustand-devtools";
import { resetAnalyticsIdentity, setAnalyticsIdentity } from "utils/analytics";
import { resetAuthFailedFlag } from "utils/axios";
import { initializeChatbase } from "utils/chatbase";

import { config } from "utils/config";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { setCompanyStore } from "./company.store";

interface AuthState {
	uid: string | null;
	token: string | null;
	loading: boolean;
	setAuth: (uid: string) => void;
	setToken: (token: string | null) => void;
	setLoading: (loading: boolean) => void;
	reset: () => void;
}

const initialState = {
	loading: false,
	uid: null,
	token: null,
};

const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			...initialState,
			setAuth: (uid) => set(() => ({ uid })),
			setToken: (token) => set(() => ({ token })),
			setLoading: (loading) => set(() => ({ loading })),
			reset: () => {
				set(initialState);
			},
		}),
		{
			name: "auth-storage", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => sessionStorage), // (optional) by default, 'localStorage' is used
		},
	),
);

const setAuthLoading = (loading: boolean) =>
	useAuthStore.getState().setLoading(loading);

const setAuth = async (uid: string | null) => {
	if (uid) {
		useAuthStore.getState().setAuth(uid);
		// Reset auth failed flag to allow API requests after successful authentication
		resetAuthFailedFlag();

		// Give the activation events an id to hang off immediately. The
		// attribution, email-verification and onboarding gates all render before
		// the app shell fetches `user`, so waiting for the numeric id would leave
		// the first three funnel steps unattributable. `useUser` upgrades this to
		// the app user id once it arrives.
		setAnalyticsIdentity({ userId: uid });

		// Initialize Chatbase after successful authentication
		try {
			// Small delay to ensure the auth state is fully set
			setTimeout(async () => {
				await initializeChatbase();
			}, 100);
		} catch (error) {
			console.error("Chatbase initialization failed:", error);
			// Don't throw - we don't want Chatbase to break the auth flow we have
		}
	} else {
		useAuthStore.getState().reset();
		resetAnalyticsIdentity();
	}

	setCompanyStore(uid || "");
};
const setToken = (token: string | null) => {
	useAuthStore.getState().setToken(token || null);
};

const getAuth = () => {
	return useAuthStore.getState().uid;
};

if (config.isLocal && config.isDev) {
	mountStoreDevtool("Auth", useAuthStore);
}

export { getAuth, setAuth, setAuthLoading, setToken, useAuthStore };
